// eslint-disable-next-line import/no-extraneous-dependencies
import React, { useState, useEffect } from 'react'
// import type { PlayerRow } from './App.tsx'
import { PlayerHeaderProps } from './utils/types.ts'

import {
    SAVANT_GREEN,
    NAME_TEXT_TOP_OFFSET,
    NAME_TEXT_OFFSET,
    YEAR_TEXT_OFFSET,
    TEAM_LOGO_OFFSET,
    TEAM_LOGO_SIZE,
    BETWEEN_PLAYERS_BUFFER,
} from './utils/constants.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'

const BASE_NAME_FONT_SIZE = 14
const MIN_NAME_FONT_SIZE = 12

// Reuse a single off-DOM canvas context for text measurement
let measureCtx: CanvasRenderingContext2D | null = null
const getMeasureCtx = () => {
    if (!measureCtx) {
        measureCtx = document.createElement('canvas').getContext('2d')
    }
    return measureCtx
}

const measureWidth = (
    ctx: CanvasRenderingContext2D,
    text: string,
    fontSize: number
) => {
    // eslint-disable-next-line no-param-reassign
    ctx.font = `${fontSize}px 'Roboto Condensed'`
    return ctx.measureText(text).width
}

// Trim a string with a trailing ellipsis so it fits within maxWidth
const truncateToWidth = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    fontSize: number
) => {
    const ellipsis = '\u2026'
    // binary search for the longest prefix that fits with the ellipsis
    let lo = 0
    let hi = text.length
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2)
        const candidate = text.slice(0, mid).trimEnd() + ellipsis
        if (measureWidth(ctx, candidate, fontSize) <= maxWidth) {
            lo = mid
        } else {
            hi = mid - 1
        }
    }
    return text.slice(0, lo).trimEnd() + ellipsis
}

// Fit text within maxWidth: try the base size, shrink to the min size,
// then fall back to an ellipsis at the min size.
const fitText = (text: string, maxWidth: number) => {
    const ctx = getMeasureCtx()
    if (!ctx) {
        return { text, fontSize: BASE_NAME_FONT_SIZE }
    }

    if (measureWidth(ctx, text, BASE_NAME_FONT_SIZE) <= maxWidth) {
        return { text, fontSize: BASE_NAME_FONT_SIZE }
    }
    if (measureWidth(ctx, text, MIN_NAME_FONT_SIZE) <= maxWidth) {
        return { text, fontSize: MIN_NAME_FONT_SIZE }
    }
    return {
        text: truncateToWidth(ctx, text, maxWidth, MIN_NAME_FONT_SIZE),
        fontSize: MIN_NAME_FONT_SIZE,
    }
}

function PlayerHeader(props: PlayerHeaderProps) {
    const { IMG_WIDTH, IMG_TOP_BUFFER } = useDimensions()

    const { data, maxSliderWidth, showTeams, isHidden } = props
    const { id, player_name, year, teams } = data
    // const imgUrl = `https://midfield.mlbstatic.com/v1/people/${id}/mlb/120?circle=true`
    // const proxyUrl = `/comparison-tool/services/api/${encodeURIComponent(
    //     imgUrl
    // )}`

    const teamsToDisplay = (teams ?? []) as number[]
    const teamLogoGap = 2
    const teamLogoStep = TEAM_LOGO_SIZE + teamLogoGap
    const totalTeamsWidth =
        teamsToDisplay.length > 0
            ? teamsToDisplay.length * TEAM_LOGO_SIZE +
              (teamsToDisplay.length - 1) * teamLogoGap
            : 0

    const nameTokens = (player_name as string).split(', ')
    const firstName = nameTokens[1]
    const lastName = nameTokens[0]

    // The name is centered, so it can use the full per-player slot including
    // half of the gap on each side before touching a neighboring player.
    const availableWidth = maxSliderWidth + BETWEEN_PLAYERS_BUFFER - 2
    const displayFirst = fitText(firstName, availableWidth)
    const displayLast = fitText(lastName, availableWidth)

    const imgX = (maxSliderWidth - IMG_WIDTH) / 2

    const [headshotBase64, setHeadshotBase64] = useState<string | null>(null)
    const [teamLogosBase64, setTeamLogosBase64] = useState<
        Record<number, string>
    >({})

    useEffect(() => {
        const mlbUrl = `https://midfield.mlbstatic.com/v1/people/${
            isHidden ? 'generic' : id
        }/mlb/120?circle=true`
        const proxyUrl = `/comparison-tool/services/api/${encodeURIComponent(
            mlbUrl
        )}`

        fetch(proxyUrl)
            .then((response) => response.blob()) // Get the raw binary blob
            .then((blob) => {
                const reader = new FileReader()
                reader.onloadend = () => {
                    setHeadshotBase64(reader.result as string)
                }
                reader.readAsDataURL(blob)
            })
            .catch((err) => console.error('Failed to load headshot', err))

        // --- FETCH TEAM LOGOS (Using Promise.all for speed) ---
        const fetchTeamLogos = async () => {
            const logoMap: Record<number, string> = {}

            await Promise.all(
                teamsToDisplay.map(async (teamId) => {
                    const teamUrl = `https://www.mlbstatic.com/team-logos/${teamId}.svg`
                    const teamProxy = `/comparison-tool/services/api/${encodeURIComponent(
                        teamUrl
                    )}`

                    try {
                        const res = await fetch(teamProxy)
                        const blob = await res.blob()

                        const base64 = await new Promise<string>((resolve) => {
                            const reader = new FileReader()
                            reader.onloadend = () =>
                                resolve(reader.result as string)
                            reader.readAsDataURL(blob)
                        })

                        logoMap[teamId] = base64
                    } catch (err) {
                        console.error(`Failed to load team ${teamId}`, err)
                    }
                })
            )
            if (isHidden) {
                setTeamLogosBase64({})
            } else {
                setTeamLogosBase64(logoMap)
            }
        }

        if (teamsToDisplay.length > 0) {
            fetchTeamLogos()
        }
    }, [id, teamsToDisplay, isHidden])

    return (
        <>
            <circle
                cx={maxSliderWidth / 2}
                cy={IMG_TOP_BUFFER + IMG_WIDTH / 2}
                r={IMG_WIDTH / 2}
                strokeWidth={4}
                stroke={SAVANT_GREEN}
                fill={SAVANT_GREEN}
                // onMouseHover={() => setIsHovered(true)}
            />
            {headshotBase64 && (
                <image
                    x={imgX}
                    y={IMG_TOP_BUFFER}
                    href={headshotBase64!}
                    width={IMG_WIDTH}
                    height={IMG_WIDTH}
                />
            )}
            {isHidden ? (
                <text
                    x={maxSliderWidth / 2}
                    y={
                        IMG_TOP_BUFFER +
                        IMG_WIDTH +
                        NAME_TEXT_TOP_OFFSET +
                        NAME_TEXT_OFFSET / 2
                    }
                    className="player-name"
                    style={{
                        fontFamily: 'Roboto Condensed',
                        fontSize: '14px',
                    }}
                >
                    ????????
                </text>
            ) : (
                <>
                    <text
                        x={maxSliderWidth / 2}
                        y={IMG_TOP_BUFFER + IMG_WIDTH + NAME_TEXT_TOP_OFFSET}
                        className="player-name"
                        style={{
                            fontFamily: 'Roboto Condensed',
                            fontSize: displayFirst.fontSize,
                        }}
                    >
                        {displayFirst.text}
                    </text>
                    <text
                        x={maxSliderWidth / 2}
                        y={
                            IMG_TOP_BUFFER +
                            IMG_WIDTH +
                            NAME_TEXT_TOP_OFFSET +
                            NAME_TEXT_OFFSET
                        }
                        className="player-name"
                        style={{
                            fontFamily: 'Roboto Condensed',
                            fontSize: displayLast.fontSize,
                        }}
                    >
                        {displayLast.text}
                    </text>
                </>
            )}
            <text
                x={maxSliderWidth / 2}
                y={
                    IMG_TOP_BUFFER +
                    IMG_WIDTH +
                    NAME_TEXT_TOP_OFFSET +
                    NAME_TEXT_OFFSET +
                    YEAR_TEXT_OFFSET
                }
                className="player-year"
                style={{ fontFamily: 'Roboto Condensed' }}
            >
                {/* {`${year}${ohtaniText}`} */}
                {year}
            </text>
            <g
                transform={`translate(${
                    maxSliderWidth / 2 - totalTeamsWidth / 2
                }, ${
                    IMG_TOP_BUFFER +
                    IMG_WIDTH +
                    NAME_TEXT_TOP_OFFSET +
                    NAME_TEXT_OFFSET +
                    YEAR_TEXT_OFFSET +
                    TEAM_LOGO_OFFSET
                })`}
            >
                {showTeams &&
                    teamsToDisplay.map((team, idx) => {
                        const logoData = teamLogosBase64[team]
                        return logoData ? (
                            <image
                                key={team}
                                x={idx * teamLogoStep}
                                y={0}
                                href={logoData}
                                width={TEAM_LOGO_SIZE}
                                height={TEAM_LOGO_SIZE}
                            />
                        ) : null
                    })}
            </g>
        </>
    )
}

export default PlayerHeader
